/**
 * Extract an 11-character YouTube video ID from any supported form.
 *
 * Handles (with any leading protocol / trailing query / fragment):
 *   - https://youtu.be/VIDEO_ID?si=…
 *   - https://www.youtube.com/watch?v=VIDEO_ID&t=…
 *   - https://www.youtube.com/embed/VIDEO_ID
 *   - https://www.youtube.com/shorts/VIDEO_ID?feature=share
 *   - https://m.youtube.com/watch?v=VIDEO_ID
 *   - bare 11-char VIDEO_ID
 *
 * Returns "" when nothing usable is found.
 */
export function extractYouTubeId(input) {
  if (!input) return "";
  const raw = String(input).trim();

  // Bare 11-char id
  if (/^[A-Za-z0-9_-]{11}$/.test(raw)) return raw;

  // Any of the known URL shapes — captures the ID before ? # or /
  const m = raw.match(
    /(?:v=|\/embed\/|\/shorts\/|youtu\.be\/|\/v\/|\/live\/)([A-Za-z0-9_-]{11})/
  );
  return m ? m[1] : "";
}

/**
 * Build the official YouTube iframe embed URL for a given video reference.
 * Uses `www.youtube.com/embed/` (the referrer-friendly canonical form).
 */
export function youtubeEmbedUrl(videoRef, opts = {}) {
  const id = extractYouTubeId(videoRef);
  if (!id) return "";
  const params = new URLSearchParams({
    rel: "0",
    modestbranding: "1",
    playsinline: "1",
    ...(opts.params || {}),
  });
  return `https://www.youtube.com/embed/${id}?${params.toString()}`;
}

/** Convenience: link students can click to open the video directly on YouTube. */
export function youtubeWatchUrl(videoRef) {
  const id = extractYouTubeId(videoRef);
  return id ? `https://www.youtube.com/watch?v=${id}` : "";
}
